import _ from 'underscore';
import { CivilDate } from 'tc39-proposal-temporal';
import {
  getCivilDateToday,
  compareCivilDate,
  // isValidDate
  getRangesFromData,
} from '@bingads-webui-universal/temporal-utitlities';
import { VideoStatus, VersionForLog } from './constants';

export const civilDateEqual = (d1, d2) => {
  if (!d1 && !d2) {
    return true;
  }
  if (!d1 || !d2) {
    return false;
  }
  return compareCivilDate(d1, d2) === 0;
};

export const civilDateRangeEqual = (r1, r2) => {
  if ((!r1 && !r2) || (r1 === r2)) {
    return true;
  }
  if (!r1 || !r2) {
    return false;
  }
  return civilDateEqual(r1.start, r2.start) && civilDateEqual(r1.end, r2.end);
};

export const civilDateToDebugString = (cd) => {
  if (!cd) return '';
  const { year, month, day } = cd || {};
  return `${year || ''}-${month || ''}-${day || ''}`;
};

export const civilDateRangeToDebugString = (dr) => {
  if (!dr) {
    return '';
  }
  return `start:${civilDateToDebugString(dr.start)} end:${civilDateToDebugString(dr.end)}`;
};

// dateStr: string in format like 2022-06-21T08:55:59.807-07:00
export const parseMTDateString = (i18n, dateStr) => {
  if (!dateStr) {
    return null;
  }
  // remove the millisecs part to avoid parsing fail (the length of millisecs part is variable)
  const str = dateStr.replace(/\.\d+/, '');
  const options = {
    raw: 'yyyy-MM-ddTHH:mm:ssZZZZZ',
    timeZone: null,
  };
  const date = i18n.parseDate(str, options);
  return date;
};

export const formatDate = (date, i18n, ianaTimeZone, dateFormat) =>
  i18n.formatDate(date, { raw: dateFormat, timeZone: ianaTimeZone });

export const findParentElementWithClass = (elem, className) => {
  let target = elem;
  while (target) {
    const { classList } = target || {};
    if (classList && classList.length > 0
      && classList.contains && classList.contains(className)) {
      return target;
    }
    target = target.parentElement;
  }
  return null;
};

export const validate = (value) => {
  const reg = /<|>|:|\*|\?|"|\\|\/|\|/;
  return !reg.test(value);
};

const getSizeLimitPerAccount = (aid, id) => {
  const value = localStorage.getItem(`SystemLimitData_${aid}`);
  if (!value) {
    return null;
  }

  let valObj = null;

  try {
    valObj = JSON.parse(value);
  } catch (e) {
    return null;
  }

  const limitData = _.find(valObj.data, data => data.id === id);

  return limitData && limitData.val;
};

export const getImageAssetLimitPerAccount = (aid, logger) => {
  const assetLimit = getSizeLimitPerAccount(aid, 32);
  if (assetLimit) {
    return assetLimit;
  }

  logger.logError('get image asset limit data error', { aid });
  return 5000;
};

export const getVideoAssetLimitPerAccount = (aid, logger) => {
  const assetLimit = getSizeLimitPerAccount(aid, 86);
  if (assetLimit) {
    return assetLimit;
  }

  logger.logError('get video asset limit data error', { aid });
  return 1000;
};

export const getFoldersLimitPerAccount = (aid, logger) => {
  const foldersLimit = getSizeLimitPerAccount(aid, 97);
  if (foldersLimit) {
    return foldersLimit;
  }
  logger.logError('get folders limit data error', { aid });
  return 1000;
};

export const keyDownHandlerForListItem = (event, itemIndex, listItems, tabTarget, enterHandler, spaceHandler, customTabHandler = undefined) => {
  switch (event.code) {
    case 'ArrowUp':
    case 'ArrowLeft':
      if (itemIndex > 0 && listItems[itemIndex - 1]) {
        listItems[itemIndex - 1].focus();
      }
      break;
    case 'ArrowDown':
    case 'ArrowRight':
      if (itemIndex < listItems.length - 1 && listItems[itemIndex + 1]) {
        listItems[itemIndex + 1].focus();
      }
      break;
    case 'Tab':
      if (customTabHandler) {
        customTabHandler(event.shiftKey);
      } else if (tabTarget) {
        tabTarget.focus();
      }
      break;
    case 'Enter':
      enterHandler();
      break;
    case 'Space':
      spaceHandler();
      break;
    default:
      break;
  }

  event.stopPropagation();
  event.preventDefault();
};

export const checkVideosIntermediateStatus =
  video => video.status !== VideoStatus.Active && video.status !== VideoStatus.Failed;

// need update this function before starting new pilot
export const getLogVersion = () => VersionForLog.V5;


const getRealUsedFlagValue = (preference, flagSuffix) => {
  if (preference && flagSuffix) {
    const realUsedFlagStr = `realUsedFlagIn${flagSuffix}`;
    const realUsedFlagHandlerStr = `setRealUsedFlagIn${flagSuffix}`;
    const realUsedFlag = preference[realUsedFlagStr];
    const realUsedFlagHandler = preference[realUsedFlagHandlerStr];

    return {
      realUsedFlag,
      realUsedFlagHandler,
    };
  }

  return {};
};

export const handleRealUsedFlag = (preference, flagSuffix) => { // flagSuffix: AssetLibraryApp or AssetLibraryPicker
  const { realUsedFlag, realUsedFlagHandler } = getRealUsedFlagValue(preference, flagSuffix);

  const {
    haveUsed,
  } = realUsedFlag || {};

  if (!haveUsed && realUsedFlagHandler) {
    realUsedFlagHandler();
  }
};

export const addAssetLibrarySurveyTriggerFlag = (preference, triggerFlag, usedFlagSuffix) => { // trigger flag: AssetLibraryPickerSurveyTrigger or AssetLibraryAppSurveyTrigger
  const { realUsedFlag } = getRealUsedFlagValue(preference, usedFlagSuffix);

  const { haveUsed, timestamp } = realUsedFlag || {};

  if (haveUsed) {
    const currentTime = new Date().getTime();
    const diffDay = (currentTime - timestamp) / 1000 / 60 / 60 / 24;
    if (diffDay > 1 && window.survey && window.survey.surveyManager) {
      window.survey[triggerFlag] = true;
      window.survey.surveyManager.trigger({
        checkIfSurveyIsOpen: true,
      });
    }
  }
};

export const removeAssetLibrarySurveyTriggerFlag = (triggerFlag) => {
  if (window.survey) {
    window.survey[triggerFlag] = false;
  }
};

export const getDefaultDateRange = () => {
  const yesterday = getCivilDateToday().plus({ days: -1 });
  const defaultRange = { start: yesterday, end: yesterday };
  return defaultRange;
};

export const getDefaultMetricDateRange = (i18n, dateFormat, timeZone, lcid) => {
  const todayObj = i18n.getToday({ timeZone });
  const today = new CivilDate(todayObj.year, todayObj.month, todayObj.day);
  const yesterday = today.plus({ days: -1 });
  const last30day = today.plus({ days: -30 });
  return {
    RangeStartDate: i18n.formatCivilDate(last30day, { raw: dateFormat, timeZone }),
    RangeEndDate: i18n.formatCivilDate(yesterday, { raw: dateFormat, timeZone }),
    Rangelcid: lcid,
    RangeId: 5, // last 30 days, RANGES.Last30Days
  };
};

const formatCivilDateString = (civilDate) => {
  if (!civilDate) return '';

  const { year, month, day } = civilDate;
  if (!year || !month || !day) return '';

  return `${year}-${month < 10 ? '0' : ''}${month}-${day < 10 ? '0' : ''}${day}`;
};

export const getDateRangeParams = (i18n, dateData, dateFormat, lcid) => {
  const { selectedRange, selectedComparedRange } = getRangesFromData(i18n, dateData, dateFormat, lcid) || {};
  if (selectedRange) {
    const startParamStr = formatCivilDateString(selectedRange.start);
    const endParamStr = formatCivilDateString(selectedRange.end);
    const comparisonStartDateStr = selectedComparedRange ? formatCivilDateString(selectedComparedRange.start) : null;
    const comparisonEndDateStr = selectedComparedRange ? formatCivilDateString(selectedComparedRange.end) : null;

    return {
      startDate: startParamStr,
      endDate: endParamStr,
      comparisonStartDate: comparisonStartDateStr,
      comparisonEndDate: comparisonEndDateStr,
    };
  }
  return {};
};
